// Phase 4 — Internal Insights engine.
//
// Rule-based, READ-ONLY summarizations of internal data already accessible
// through session-authenticated endpoints (no admin-key, no LLM call).
//
// Inputs:
//   prospects[]   — from GET /api/prospects (session-auth, CEO/Sales/Analyst)
//   dashboard     — from GET /api/internal/dashboard (session-auth, CEO/Sales/Analyst)
//
// Outputs: structured insight cards the Operations/AiOps pages render.
// Never mutates anything. Never calls an LLM. Never sends email.

const DAY_MS = 86_400_000;

const daysSince = (iso) => {
  if (!iso) return Infinity;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return Infinity;
  return Math.floor((Date.now() - t) / DAY_MS);
};

export function computeInsights({ prospects = [], dashboard = null }) {
  const out = {
    sales_priorities: [],
    stalled: [],
    hot_prospects: [],
    discovery_signal: null,
    lead_quality: null,
    customer_activity: null,
    system_health: [],
    suggested_actions: [],
  };

  // -------- Sales priorities (Immediate / Discovery Scheduled / Proposal Sent) --------
  const PRIORITY_STAGES = new Set(["Contacted", "Replied", "Assessment Requested", "Assessment Delivered"]);
  const priority = prospects.filter((p) => PRIORITY_STAGES.has(p.stage));
  // Sort by intelligence_report total_score (desc), then by updated_at (desc)
  priority.sort((a, b) => {
    const sa = a?.intelligence_report?.total_score ?? 0;
    const sb = b?.intelligence_report?.total_score ?? 0;
    if (sb !== sa) return sb - sa;
    return (b.updated_at || "").localeCompare(a.updated_at || "");
  });
  out.sales_priorities = priority.slice(0, 6).map((p) => ({
    id: p.prospect_id,
    name: p.company_name,
    stage: p.stage,
    score: p.intelligence_report?.total_score ?? p.score ?? null,
    grade: p.grade ?? null,
    priority: p.intelligence_report?.priority ?? null,
    days_idle: daysSince(p.updated_at),
  }));

  // -------- Stalled prospects (no activity > 14 days, not in terminal stages) --------
  const TERMINAL = new Set(["Paid", "Repeat Customer", "Lost Opportunity"]);
  const stalled = prospects
    .filter((p) => !TERMINAL.has(p.stage) && daysSince(p.updated_at) >= 14)
    .sort((a, b) => daysSince(b.updated_at) - daysSince(a.updated_at))
    .slice(0, 6);
  out.stalled = stalled.map((p) => ({
    id: p.prospect_id,
    name: p.company_name,
    stage: p.stage,
    days_idle: daysSince(p.updated_at),
  }));

  // -------- Hot prospects (grade A or intel-priority Immediate) --------
  const hot = prospects
    .filter((p) => (p.grade === "A") || (p.intelligence_report?.priority === "Immediate"))
    .sort((a, b) => (b.intelligence_report?.total_score ?? b.score ?? 0) - (a.intelligence_report?.total_score ?? a.score ?? 0))
    .slice(0, 5);
  out.hot_prospects = hot.map((p) => ({
    id: p.prospect_id,
    name: p.company_name,
    score: p.intelligence_report?.total_score ?? p.score,
    grade: p.grade,
    priority: p.intelligence_report?.priority,
  }));

  // -------- Lead quality snapshot (from dashboard totals if present) --------
  if (dashboard?.totals) {
    out.lead_quality = {
      prospects: dashboard.totals.prospects ?? 0,
      qualified: dashboard.totals.qualified ?? 0,
      qualified_rate: (dashboard.totals.prospects > 0)
        ? Math.round(((dashboard.totals.qualified || 0) / dashboard.totals.prospects) * 100)
        : 0,
      paid_customers: dashboard.totals.paid_customers ?? 0,
    };
  }

  // -------- Customer activity snapshot --------
  if (dashboard?.totals) {
    out.customer_activity = {
      paid_customers: dashboard.totals.paid_customers ?? 0,
      repeat_customers: dashboard.totals.repeat_customers ?? 0,
      assessments_delivered: dashboard.totals.assessments_delivered ?? 0,
    };
  }

  // -------- Discovery signal (placeholder until /api/events is session-auth) --------
  out.discovery_signal = {
    is_placeholder: true,
    note: "Public AI Discovery starts/completions/fit-summaries are tracked to /api/events. A session-authenticated read path for CEO is not yet exposed (Phase 5).",
  };

  // -------- System health --------
  out.system_health = [
    { label: "Scoring engine",       status: "ok",      detail: "Deterministic formula pinned. Temperature 0." },
    { label: "Intelligence Report",  status: "ok",      detail: "Additive endpoint. CRM grade unaffected." },
    { label: "Prospect search",      status: "ok",      detail: "Server-side q= across 18 fields." },
    { label: "Customer Platform",    status: "pending", detail: "Phase 3 — greenfield. Customer role still unused." },
    { label: "Stripe webhook sig.",  status: "todo",    detail: "Polling fallback in use. Phase 5." },
    { label: "Admin-key auth",       status: "todo",    detail: "Query-param mode. Move to header in Phase 5." },
    { label: "Public rate-limiting", status: "todo",    detail: "/leads, /events, /extract unprotected. Phase 5." },
  ];

  // -------- Suggested actions (the spec's exact list, conditional) --------
  const recentDiscoveryHint = out.sales_priorities.length === 0
    ? "Follow up with new AI Discovery leads — none in CRM yet, they're landing in /api/leads with source='discovery'."
    : `Follow up with ${out.sales_priorities.length} prospect(s) currently in active stages.`;

  out.suggested_actions = [
    { label: recentDiscoveryHint, todo: false },
    { label: out.hot_prospects.length > 0 ? `Review ${out.hot_prospects.length} high-fit prospect(s) (Grade A or Immediate priority)` : "No Grade-A / Immediate-priority prospects yet — run more Intelligence Reports", todo: false },
    { label: out.stalled.length > 0 ? `Check ${out.stalled.length} stalled prospect(s) (>14 days idle)` : "No stalled prospects (>14 days idle)", todo: false },
    { label: "Review recent Sample Report viewers (needs session-auth /api/events read — Phase 5)", todo: true },
    { label: "Add real demo videos (placeholders live in data/videoLibrary.js)", todo: true },
    { label: "Complete Academy article bodies (stubs in data/learningArticles.js)", todo: true },
    { label: "Confirm strategy-call email (currently TODO: hello@govbidiq.com placeholder)", todo: true },
    { label: "Add rate limiting to public lead forms in Phase 5", todo: true },
    { label: "Harden Stripe webhook signature in Phase 5", todo: true },
    { label: "Move ADMIN_KEY diagnostics to header in Phase 5", todo: true },
    { label: "Prepare customer auth design (magic-link) for Phase 3", todo: true },
  ];

  return out;
}
